import {
  faceOf, parseKey, type MatchConfig, type ScoreBreakdown, type Tile, type WireState,
} from '@nt2/engine';
import { VERSION } from '../version.js';

/**
 * Every finished match, kept on this device.
 *
 * Plain JSON in local storage, so it can be read, exported and shared without
 * the game: who played, the rules, each grid as a list of letters with their
 * squares, and how each score was made. Nothing here is sent anywhere unless
 * the player exports or shares it themselves.
 */

const KEY = 'nt2.history.v1';
/** Enough for months of play, and still far inside local storage's limits. */
const LIMIT = 200;

export interface HistoryLetter {
  x: number;
  y: number;
  letter: string | null;
  blank: boolean;
  value: number;
}

export interface HistoryPlayer {
  seatId: string;
  name: string;
  you: boolean;
  score: number;
  won: boolean;
  wentOut: boolean;
  lettersPlaced: number;
  lettersInHand: number;
  /** Null when this grid stayed private in the match. */
  grid: HistoryLetter[] | null;
  /** How the score was made: every word, crossings counted twice, penalties, bonus. */
  scoring: {
    /** Each word once, where it starts and which way it runs. */
    words: Array<{ word: string; direction: string; x: number; y: number; points: number; valid: boolean }>;
    wordPoints: number;
    penalty: number;
    bonus: number;
    total: number;
  } | null;
}

export interface GameRecord {
  /** Unique per match: the table code and when it started. */
  id: string;
  format: 'puletiwo-game';
  formatVersion: 1;
  app: string;
  kind: 'solo' | 'table';
  table: string | null;
  startedAt: string | null;
  finishedAt: string;
  rounds: number;
  rules: Omit<MatchConfig, 'seed'>;
  players: HistoryPlayer[];
  /** The table's running totals at the end of this match, when there were several. */
  tableTotals: WireState['series'] | null;
}

export interface ScoredRow {
  seatId: string;
  name: string;
  you: boolean;
  score: number;
  wentOut: boolean;
  tilesPlaced: number;
  tilesInHand: number;
  breakdown: ScoreBreakdown | null;
}

const letterOf = (k: string, t: Tile): HistoryLetter => {
  const c = parseKey(k);
  return { x: c.x, y: c.y, letter: faceOf(t), blank: t.isBlank, value: t.value };
};

/** Turn a finished match into a record. */
export function recordFrom(wire: WireState, rows: readonly ScoredRow[], solo: boolean): GameRecord {
  const { seed: _seed, ...rules } = wire.config;
  const players = rows.map((r): HistoryPlayer => {
    const seat = wire.seats.find((s) => s.id === r.seatId);
    const b = r.breakdown;
    return {
      seatId: r.seatId, name: r.name, you: r.you, score: r.score,
      won: wire.winnerSeatId === r.seatId, wentOut: r.wentOut,
      lettersPlaced: r.tilesPlaced, lettersInHand: r.tilesInHand,
      grid: seat?.grid ? seat.grid.map(([k, t]) => letterOf(k, t)) : null,
      scoring: b ? {
        words: b.words.map((w) => ({
          word: w.word, direction: w.orientation, x: w.start.x, y: w.start.y,
          points: w.points, valid: w.valid,
        })),
        wordPoints: b.wordPoints, penalty: b.penalty, bonus: b.bonus, total: b.total,
      } : null,
    };
  });
  return {
    id: `${wire.matchId}:${wire.startedAt ?? wire.finishedAt ?? Date.now()}`,
    format: 'puletiwo-game', formatVersion: 1,
    app: `v${VERSION.label}`,
    kind: solo ? 'solo' : 'table',
    table: solo ? null : wire.matchId.toUpperCase(),
    startedAt: wire.startedAt ? new Date(wire.startedAt).toISOString() : null,
    finishedAt: new Date(wire.finishedAt ?? Date.now()).toISOString(),
    rounds: wire.roundsTaken,
    rules,
    players,
    tableTotals: wire.series && wire.series.games > 1 ? wire.series : null,
  };
}

export function readHistory(): GameRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) as unknown : [];
    return Array.isArray(list) ? list as GameRecord[] : [];
  } catch {
    return [];
  }
}

/** Keep a finished match, once. Newest first. */
export function saveGame(record: GameRecord): void {
  const list = readHistory().filter((g) => g.id !== record.id);
  list.unshift(record);
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, LIMIT)));
  } catch {
    // Full or blocked storage: drop the oldest half and try once more.
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, LIMIT / 2))); } catch { /* give up quietly */ }
  }
}

export function clearHistory(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/** A grid as rows of letters, for reading at a glance ("·" for an empty square). */
export function gridText(grid: readonly HistoryLetter[] | null): string {
  if (!grid || grid.length === 0) return '';
  const xs = grid.map((l) => l.x), ys = grid.map((l) => l.y);
  const x0 = Math.min(...xs), y0 = Math.min(...ys);
  const w = Math.max(...xs) - x0 + 1, h = Math.max(...ys) - y0 + 1;
  const rows = Array.from({ length: h }, () => Array.from({ length: w }, () => '·'));
  for (const l of grid) rows[l.y - y0]![l.x - x0] = l.letter ? (l.blank ? l.letter.toLowerCase() : l.letter) : '?';
  return rows.map((r) => r.join(' ')).join('\n');
}
